import React, { FunctionComponent, useRef } from 'react';
import moment from 'moment';
import { Grid, Header } from 'semantic-ui-react';
import withStyles from 'react-jss';
import { isEmpty, pathOr, map, addIndex } from 'ramda';
import {
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
  YAxis,
  XAxis,
  LineChart,
  Line,
} from 'recharts';

import Legend from 'components/Legend';
import TickFormatter from 'components/TickFormatter';
import { useUserMgmt } from 'components/UserMgmt/UserMgmt.context';
import { ClassProp } from 'helpers/types';
import {
  QueryResponse,
  getResults,
  getAverages,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/KeyMetricGraphs/KeyMetricGraphs.helpers';

import styles from './Attribution.styles';

import {
  getChartLegend,
  ViewDropDown,
  Event,
  GraphData,
} from './Attribution.helper';
import { Timeslice } from 'scenes/Report/Report.helper';

type Styles = ClassProp<typeof styles>;

type Props = {
  classes: Styles;
  response: QueryResponse;
  timeslice: Timeslice;
  dataSource: string;
};

const INTERVAL_LIMIT = 31;
const mapIndexed = addIndex(map);

const formatData = (path: string[]) =>
  map((event: Event) => ({
    eventDate: moment(event.eventDate, 'YYYY-MM-DD').valueOf(),
    value: pathOr(0, path, event),
  }));

const formatter = (value: string) => `${value}%`;

const renderLineSeries = (events: Event[], view: string) => ({
  path,
  color,
  hidden,
}: any) => {
  if (hidden) return <></>;

  const data: GraphData[] = formatData([view, path])(events);
  const name = `${view}_${path}`;
  return (
    <Line
      dataKey="value"
      data={data}
      name={name}
      key={name}
      fill={color}
      stroke={color}
      dot={false}
      strokeWidth={2}
    />
  );
};

const Attribution: FunctionComponent<Props> = ({
  classes,
  response,
  timeslice,
  dataSource,
}) => {
  const { isExternalUser } = useUserMgmt();
  const view: ViewDropDown = 'thirtyDays';
  const contentRef = useRef(null);
  const data = getResults(dataSource)(response);
  const averages = getAverages(response);
  const avgAttribution = Math.round(pathOr<number>(0, [view], averages));

  const interval = data.length > INTERVAL_LIMIT ? 'preserveStartEnd' : 0;
  const chartLegend = getChartLegend(isExternalUser);

  return (
    <div ref={contentRef} className={classes.ref}>
      <Grid.Row centered className={classes.chartContent}>
        <Grid>
          <Grid.Row className={classes.chartContent}>
            {isEmpty(data) ? (
              <Header as="h3">There is no data to display at this time.</Header>
            ) : (
              <div className={classes.chart}>
                <ResponsiveContainer>
                  <LineChart>
                    <CartesianGrid vertical={false} />
                    <YAxis dataKey="value" tickFormatter={formatter} />

                    <XAxis
                      type="number"
                      dataKey="eventDate"
                      scale="time"
                      interval={interval}
                      domain={['auto', 'auto']}
                      allowDuplicatedCategory={false}
                      tick={<TickFormatter xAxis="eventDate" data={data} />}
                    />

                    {mapIndexed(renderLineSeries(data, view), chartLegend)}
                    <ReferenceLine
                      y={avgAttribution}
                      isFront
                      stroke="red"
                      strokeDasharray="7 3"
                      label={
                        {
                          position: 'left',
                          value: `${avgAttribution}%`,
                          fill: 'red',
                          fontSize: 15,
                        } as any
                      }
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </Grid.Row>
          {!isExternalUser && (
            <Grid.Row className={classes.footerBlock}>
              <Legend legend={chartLegend} />
            </Grid.Row>
          )}
        </Grid>
      </Grid.Row>
    </div>
  );
};

export default withStyles(styles)(Attribution);
